var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import React, { useCallback, useEffect, useReducer } from "react";
import { useTranslation } from "react-i18next";
import { callAPI } from "../../lib/common";
import { useWebshopApiClient } from "@/react/Webshop/providers/WebshopApiClientProvider";
export const ACTION_START_AUTHENTICATING = "startAuthentication";
export const ACTION_STOP_AUTHENTICATING = "stopAuthentication";
export const ACTION_LOGIN = "login";
export const ACTION_AUTHENTICATION_FAILED = "authFailed";
export const ACTION_SET_LANGUAGE = "setLanguage";
export const ACTION_SET_ANONYMOUS_USER = "setAnonymousUser";
export const ACTION_SET_PATCHING = "setPatching";
export const ACTION_PATCH_USER = "patchUser";
export const ACTION_TERMS_OF_SERVICE_EXPIRED = "termsOfServiceExpired";
export const ACTION_LOGOUT = "logOut";
export const ACTION_SET_SESSION_EXPIRED = "setSessionExpired";
export const ACTION_SET_REFERRER = "setReferrer";
const authReducer = (state, action) => {
    switch (action.type) {
        case ACTION_LOGOUT:
            return Object.assign(Object.assign({}, state), { user: null, isAuthenticating: false, isAuthenticated: false, sessionExpired: false });
        case ACTION_SET_REFERRER:
            return Object.assign(Object.assign({}, state), { referrer: action.payload.referrer });
        case ACTION_SET_SESSION_EXPIRED:
            return Object.assign(Object.assign({}, state), { user: null, isAuthenticating: false, isAuthenticated: false, sessionExpired: action.payload.value });
        case ACTION_SET_PATCHING: {
            return Object.assign(Object.assign({}, state), { isPatching: action.patching });
        }
        case ACTION_PATCH_USER: {
            return Object.assign(Object.assign({}, state), { user: Object.assign({}, action.payload.user) });
        }
        case ACTION_TERMS_OF_SERVICE_EXPIRED:
            return Object.assign(Object.assign({}, state), { user: Object.assign(Object.assign({}, state.user), { termsOfServiceExpired: action.value }) });
        case ACTION_SET_ANONYMOUS_USER:
            return Object.assign(Object.assign({}, state), { user: {
                    isAnonymousUser: true,
                    lang: action.payload.language,
                }, isAuthenticating: false, isAuthenticated: false });
        case ACTION_LOGIN:
            return Object.assign(Object.assign({}, state), { user: action.user, isAuthenticating: false, isAuthenticated: !action.user.isAnonymousUser });
        case ACTION_AUTHENTICATION_FAILED:
            return Object.assign(Object.assign({}, state), { user: null, loginError: action.error });
        case ACTION_START_AUTHENTICATING:
            return Object.assign(Object.assign({}, state), { loginError: null, isAuthenticating: true });
        case ACTION_STOP_AUTHENTICATING:
            return Object.assign(Object.assign({}, state), { isAuthenticating: false });
        case ACTION_SET_LANGUAGE:
            console.log("Setting language", state, action.language);
            return Object.assign(Object.assign({}, state), { user: Object.assign(Object.assign({}, state.user), { lang: action.language.replace("-", "_") }) });
        default:
            return state;
    }
};
const initialState = {
    isAuthenticating: true,
    isAuthenticated: false,
    sessionExpired: false,
    isPatching: false,
    user: null,
    loginError: null,
    termsOfServiceExpired: false,
    referrer: window.LOGIN_REFERRER || null,
};
export const AuthContext = React.createContext(null);
const AuthProvider = ({ children }) => {
    const [state, dispatch] = useReducer(authReducer, initialState);
    const { i18n, t } = useTranslation();
    const apiClient = useWebshopApiClient();
    const loadCurrentUser = useCallback(() => {
        apiClient
            .getCurrentUser()
            .then((json) => {
            if (json.user) {
                dispatch({
                    type: ACTION_LOGIN,
                    user: json.user,
                });
            }
        })
            .finally(() => {
            dispatch({
                type: ACTION_STOP_AUTHENTICATING,
            });
        });
    }, []);
    // FIXME: This is a superugly hack to try to intercept failed requests, typical
    //        auth no longer valid as each call don't have proper checking.
    //        Should be replaced with proper handeling of failed requests
    const monkeyPatchFetch = () => {
        const { fetch: originalFetch } = window;
        window.fetch = (...args) => __awaiter(void 0, void 0, void 0, function* () {
            const [resource, config] = args;
            const response = yield originalFetch(resource, config);
            // response interceptor
            const json = () => response
                .clone()
                .json()
                .then((data) => {
                if (typeof data === "object" &&
                    (data.user_error == "USER_NOT_LOGGED_IN" ||
                        data.user_error == "SESSION_TIMEOUT")) {
                    dispatch({
                        type: ACTION_SET_SESSION_EXPIRED,
                        payload: {
                            value: true,
                        },
                    });
                }
                return data;
            });
            response.json = json;
            if (response.status == 401) {
                dispatch({
                    type: ACTION_SET_SESSION_EXPIRED,
                    payload: {
                        value: true,
                    },
                });
            }
            console.log("Monkey patch response", response);
            return response;
        });
    };
    // On page load, set user status:
    useEffect(() => {
        monkeyPatchFetch();
        const wdow = window;
        if (wdow.NTLF_CONFIG.user) {
            if (window.NTLF_CONFIG.user.isAnonymous) {
                dispatch({
                    type: ACTION_SET_ANONYMOUS_USER,
                    payload: {
                        language: wdow.NTLF_CONFIG.user.lang,
                    },
                });
            }
            else {
                dispatch({
                    type: ACTION_LOGIN,
                    user: wdow.NTLF_CONFIG.user,
                });
            }
            return;
        }
        loadCurrentUser();
    }, []);
    const login = (username, password) => {
        dispatch({
            type: ACTION_START_AUTHENTICATING,
        });
        return callAPI("/ajax/login.php", "POST", {
            username,
            password,
            context: "react-mobile",
        })
            .then((json) => {
            var _a;
            if (json.user) {
                dispatch({
                    type: ACTION_LOGIN,
                    user: json.user,
                });
                const userI18NLang = (_a = json.user.lang) === null || _a === void 0 ? void 0 : _a.replace("_", "-");
                console.log("User lang", json.user.lang, i18n.language);
                if (userI18NLang && userI18NLang != i18n.language) {
                    i18n.changeLanguage(userI18NLang);
                }
                return json;
            }
            dispatch({
                type: ACTION_AUTHENTICATION_FAILED,
                error: json.error || t("Unable to authenticate"),
            });
            return Promise.reject(json);
        })
            .catch((err) => {
            console.log("ERrorr", err);
            if (typeof err === "object" &&
                err !== null &&
                ("warning" in err || "error" in err)) {
                return Promise.reject(err);
            }
            dispatch({
                type: ACTION_AUTHENTICATION_FAILED,
                error: t("Unable to authenticate"),
            });
            return Promise.reject(err);
        })
            .finally(() => {
            dispatch({
                type: ACTION_STOP_AUTHENTICATING,
            });
        });
    };
    const setLanguage = (language) => {
        return new Promise((resolve, reject) => {
            // Server language, fire and forget?
            callAPI(`/ajax/user/set_language.php?language=${language.long}`, "GET")
                .then((data) => {
                // Set the local language?
                const i18NextLang = language.long.replace("_", "-");
                i18n.changeLanguage(i18NextLang);
                dispatch({
                    type: ACTION_SET_LANGUAGE,
                    language: i18NextLang,
                });
                resolve(true);
            })
                .catch((err) => {
                reject(err);
            });
        });
    };
    const logout = () => {
        callAPI("/ajax/logout.php").then(() => {
            sessionStorage.clear();
            sessionStorage.setItem("justLoggedOut", "true");
            dispatch({
                type: ACTION_LOGOUT,
            });
        });
    };
    const setReferrer = (referrer) => {
        dispatch({
            type: ACTION_SET_REFERRER,
            payload: {
                referrer,
            },
        });
    };
    const acceptTermsOfService = () => {
        callAPI("/api/webshop/user/accept-terms-of-service", "PUT").then(() => {
            dispatch({
                type: ACTION_TERMS_OF_SERVICE_EXPIRED,
                value: false,
            });
        });
    };
    const patchUser = (data) => {
        dispatch({
            type: ACTION_SET_PATCHING,
            patching: true,
        });
        console.log("Patching data", data);
        callAPI("/api/webshop/user", "PATCH", data)
            .then((data) => {
            dispatch({
                type: ACTION_PATCH_USER,
                payload: data,
            });
        })
            .finally(() => {
            dispatch({
                type: ACTION_SET_PATCHING,
                patching: false,
            });
        });
    };
    return (React.createElement(AuthContext.Provider, { value: Object.assign(Object.assign({}, state), { loadCurrentUser,
            setReferrer,
            dispatch,
            login,
            logout,
            setLanguage,
            acceptTermsOfService,
            patchUser }) }, children));
};
export default AuthProvider;
export const useAuth = () => {
    const context = React.useContext(AuthContext);
    if (!context) {
        throw new Error("useAuth must be used within a AuthProvider");
    }
    return context;
};
